import React, { useState, useEffect } from 'react';
import { getDatabase, ref, set, onValue, remove } from 'firebase/database';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  Delete, Download, Edit, FileText, User, Calendar, Settings,
} from 'react-feather';
import {
  Modal, Button, ConfigProvider, Popconfirm, message,
  Card, Form, Input, Select, Row, Col, Typography, Divider, Empty, Tag, Space, Tooltip, Dropdown,
} from 'antd';
import { DownOutlined } from '@ant-design/icons';
import QRCode from 'qrcode';

import cirepLogo from './cirep-logo.png';
import troiaLogo from './troia-logo.png';
import italianBorder from './italian-border.png';
import sigJeanBaptiste from './sig-jean-baptiste.png';
import sigBortone from './sig-bortone.png';
import sigCaserta from './sig-caserta.png';
import { DEFAULT_ITALIAN_TEMPLATE, mergeItalianTemplate, getItalianLocaleTemplate } from './italianCertificateTemplate';
import { applyMergeTemplate, buildItalianMergeData } from './italianMergeFieldUtils';
import ItalianTemplateEditorModal from './ItalianTemplateEditorModal';
import './certificate.css';
import './italianCertificate.css';

const { Title, Text } = Typography;

const TEMPLATE_PATH = 'italian-certificate-template/CIERHA/config';
const DOCS_PATH = 'italian-certificate/CIERHA/';

const defaultAssets = {
  leftLogo: cirepLogo,
  rightLogo: troiaLogo,
  border: italianBorder,
  signature1Image: sigJeanBaptiste,
  signature2Image: sigBortone,
  signature3Image: sigCaserta,
};

const waitForImages = (root) => Promise.all(
  Array.from(root.querySelectorAll('img')).map((img) => new Promise((resolve) => {
    if (img.complete) resolve();
    else { img.onload = resolve; img.onerror = resolve; }
  })),
);

const buildItalianCertificateQrData = (doc) => [
  'CERTIFICAT / CERTIFICATO DI LINGUA ITALIANA - CIREP',
  `Nom / Nome: ${doc.noms || ''}`,
  `Genre: ${doc.genre || ''}`,
  `Lieu de naissance / Luogo di nascita: ${doc.lieuNaissance || ''}`,
  `Date de naissance / Data di nascita: ${doc.dateNaissance || ''}`,
  `Durée du cours / Durata corso: ${doc.dureeCours || ''}`,
  `Lieu d'émission / Luogo di emissione: ${doc.lieuEmission || ''}`,
  `Date d'émission / Data di emissione: ${doc.dateEmission || ''}`,
  'Statut / Status: Document Verified',
  'Émis par / Emesso da: Centre Interuniversitaire de Recherche Pluridisciplinaire (CIREP)',
].join('\n');

const captureItalianPage = async (sourcePage) => {
  const sandbox = document.createElement('div');
  sandbox.setAttribute('aria-hidden', 'true');
  sandbox.style.cssText = 'position:fixed;left:-10000px;top:0;pointer-events:none;';

  const clone = sourcePage.cloneNode(true);
  clone.style.transform = 'none';
  clone.style.margin = '0';
  clone.style.boxShadow = 'none';

  sandbox.appendChild(clone);
  document.body.appendChild(sandbox);

  try {
    await waitForImages(clone);
    await new Promise((r) => setTimeout(r, 200));
    return html2canvas(clone, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#FFFFFF',
      width: clone.offsetWidth,
      height: clone.offsetHeight,
    });
  } finally {
    document.body.removeChild(sandbox);
  }
};

const ItalianCertificate = ({ app }) => {
  const [form] = Form.useForm();
  const [template, setTemplate] = useState(DEFAULT_ITALIAN_TEMPLATE);
  const [documents, setDocuments] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const db = getDatabase(app);

  useEffect(() => {
    onValue(ref(db, TEMPLATE_PATH), (snapshot) => {
      setTemplate(mergeItalianTemplate(snapshot.val()));
    });
  }, [db]);

  useEffect(() => {
    onValue(ref(db, DOCS_PATH), (snapshot) => {
      const data = snapshot.val();
      setDocuments(data ? Object.keys(data).map((key) => ({ id: key, ...data[key] })) : []);
    });
  }, [db]);

  useEffect(() => {
    const generateQRCode = async () => {
      if (!selectedDocument) return;
      try {
        setQrCodeData(await QRCode.toDataURL(buildItalianCertificateQrData(selectedDocument), {
          width: 120,
          margin: 1,
          color: { dark: '#000000', light: '#FFFFFF' },
        }));
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    };
    if (selectedDocument) generateQRCode();
  }, [selectedDocument]);

  const resetForm = () => {
    const defaults = {
      genre: 'Femme',
      noms: '',
      lieuNaissance: '',
      dateNaissance: '',
      dureeCours: '3 mois',
      lieuEmission: 'troia',
      dateEmission: new Date().toISOString().split('T')[0],
    };
    form.setFieldsValue(defaults);
    setIsEditing(false);
    setEditingId(null);
  };

  const handleSubmit = (values) => {
    const required = ['noms', 'lieuNaissance', 'dateNaissance', 'dureeCours', 'lieuEmission', 'dateEmission'];
    for (const field of required) {
      if (!values[field]?.trim?.()) {
        message.warning(`${field} est obligatoire!`);
        return;
      }
    }

    const docRef = isEditing
      ? ref(db, `${DOCS_PATH}${editingId}`)
      : ref(db, `${DOCS_PATH}${values.noms.replace(/\s+/g, '_')}_${Date.now()}`);

    set(docRef, isEditing ? values : { ...values, createdAt: new Date().toISOString() })
      .then(() => {
        message.success(isEditing ? 'Document modifié' : 'Certificat créé');
        resetForm();
      })
      .catch(() => message.error('Erreur lors de la sauvegarde'));
  };

  const handleEditDocument = (doc) => {
    form.setFieldsValue({
      genre: doc.genre || 'Femme',
      noms: doc.noms || '',
      lieuNaissance: doc.lieuNaissance || '',
      dateNaissance: doc.dateNaissance || '',
      dureeCours: doc.dureeCours || '3 mois',
      lieuEmission: doc.lieuEmission || 'troia',
      dateEmission: doc.dateEmission || new Date().toISOString().split('T')[0],
    });
    setIsEditing(true);
    setEditingId(doc.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveTemplate = (data) => {
    set(ref(db, TEMPLATE_PATH), { ...data, updatedAt: new Date().toISOString() })
      .then(() => {
        message.success('Modèle enregistré');
        setTemplateModalVisible(false);
      })
      .catch(() => message.error('Erreur lors de la sauvegarde du modèle'));
  };

  const downloadPDF = async (mode = 'both') => {
    if (!selectedDocument) return;

    const pageSelectors = {
      both: ['.italian-cert-page-it', '.italian-cert-page-fr'],
      it: ['.italian-cert-page-it'],
      fr: ['.italian-cert-page-fr'],
    }[mode];

    if (!pageSelectors?.length) return;

    try {
      const pdf = new jsPDF('l', 'pt', 'a4');
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();

      for (let i = 0; i < pageSelectors.length; i += 1) {
        const page = document.querySelector(`#italian-certificate-document ${pageSelectors[i]}`);
        if (!page) continue;

        const canvas = await captureItalianPage(page);
        if (i > 0) pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pw, ph);
      }

      const suffix = mode === 'both' ? 'italian-french' : mode;
      pdf.save(`${selectedDocument.noms?.replace(/\s+/g, '_') || 'italian'}_${suffix}_certificate.pdf`);
    } catch (error) {
      message.error('Erreur PDF');
    }
  };

  const downloadMenuItems = [
    { key: 'both', label: 'Italien-Français (2 pages)' },
    { key: 'fr', label: 'Français' },
    { key: 'it', label: 'Italien' },
  ];

  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#045cb4', borderRadius: 8 } }}>
      <div className="cierha-certificate-container">
        <div className="cierha-workspace">
          <div className="cierha-workspace-main">
            <Card className="cierha-form-card" bordered={false}>
              <div className="cierha-form-hero">
                <div className="italian-form-logos">
                  <img src={template.leftLogoUrl || cirepLogo} alt="CIREP" className="italian-form-logo" />
                  <img src={template.rightLogoUrl || troiaLogo} alt="Troia" className="italian-form-logo" />
                </div>
                <div className="cierha-form-hero-text">
                  <Title level={3} className="cierha-form-title">Certificat de Langue Italienne</Title>
                </div>
                <Button icon={<Settings size={16} />} size="large" onClick={() => setTemplateModalVisible(true)} className="cierha-template-btn">
                  Modifier le document
                </Button>
              </div>

              <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{
                genre: 'Femme', dureeCours: '3 mois', lieuEmission: 'troia',
                dateEmission: new Date().toISOString().split('T')[0],
              }} className="cierha-ant-form">
                <Divider orientation="left" className="cierha-section-divider">
                  <Space><User size={16} /> Informations du candidat</Space>
                </Divider>
                <Row gutter={[20, 0]}>
                  <Col xs={24} md={8}>
                    <Form.Item label="Genre" name="genre" rules={[{ required: true }]}>
                      <Select size="large" options={[
                        { value: 'Femme', label: 'Femme (Madame)' },
                        { value: 'Homme', label: 'Homme (Monsieur)' },
                      ]} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={16}>
                    <Form.Item label="Nom complet" name="noms" rules={[{ required: true }]}>
                      <Input size="large" placeholder="Ex: Yameogo Ernestine" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[20, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item label="Lieu de naissance" name="lieuNaissance" rules={[{ required: true }]}>
                      <Input size="large" placeholder="Ex: Burkina Faso" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Date de naissance" name="dateNaissance" rules={[{ required: true }]}>
                      <Input size="large" type="date" />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider orientation="left" className="cierha-section-divider">
                  <Space><FileText size={16} /> Informations du cours</Space>
                </Divider>
                <Row gutter={[20, 0]}>
                  <Col xs={24} md={8}>
                    <Form.Item label="Durée du cours" name="dureeCours" rules={[{ required: true }]}>
                      <Input size="large" placeholder="Ex: 3 mois" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label="Lieu d'émission" name="lieuEmission" rules={[{ required: true }]}>
                      <Input size="large" placeholder="Ex: troia" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label="Date d'émission" name="dateEmission" rules={[{ required: true }]}>
                      <Input size="large" type="date" />
                    </Form.Item>
                  </Col>
                </Row>

                <div className="cierha-form-actions">
                  <Button type="primary" htmlType="submit" size="large" className="cierha-submit-btn">
                    {isEditing ? 'Enregistrer les modifications' : 'Créer le certificat'}
                  </Button>
                  {isEditing && <Button size="large" onClick={resetForm}>Annuler</Button>}
                </div>
              </Form>
            </Card>
          </div>

          <aside className="cierha-workspace-sidebar">
            <Card className="cierha-list-card" bordered={false} title={(
              <Space><Calendar size={18} /><span>Certificats enregistrés</span><Tag color="blue">{documents.length}</Tag></Space>
            )}>
              {documents.length === 0 ? (
                <Empty description="Aucun certificat" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <div className="cierha-doc-list">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="cierha-doc-row"
                      onClick={() => { setSelectedDocument(doc); setModalVisible(true); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { setSelectedDocument(doc); setModalVisible(true); } }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="cierha-doc-row-info">
                        <Text strong className="cierha-doc-name">{doc.noms}</Text>
                        <Text type="secondary" className="cierha-doc-meta">{doc.lieuEmission} · {doc.dureeCours}</Text>
                      </div>
                      <div className="cierha-doc-row-actions" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Modifier">
                          <Button type="text" icon={<Edit size={16} />} onClick={() => handleEditDocument(doc)} />
                        </Tooltip>
                        <Popconfirm title="Supprimer ?" onConfirm={() => remove(ref(db, `${DOCS_PATH}${doc.id}`))} okText="Oui" cancelText="Non" okButtonProps={{ danger: true }}>
                          <Tooltip title="Supprimer"><Button type="text" danger icon={<Delete size={16} />} /></Tooltip>
                        </Popconfirm>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </aside>
        </div>

        <Modal
          title={<span style={{ color: 'white' }}>Aperçu — Certificat de Langue Italienne</span>}
          open={modalVisible}
          onCancel={() => { setModalVisible(false); setSelectedDocument(null); }}
          width="95vw"
          style={{ top: 10 }}
          classNames={{ body: 'italian-cert-modal-body' }}
          styles={{ body: { overflow: 'visible', maxHeight: 'none' } }}
          footer={[
            <Dropdown
              key="dl"
              menu={{
                items: downloadMenuItems,
                onClick: ({ key }) => downloadPDF(key),
              }}
            >
              <Button type="primary" icon={<Download size={16} />} style={{ backgroundColor: '#28a745', borderColor: '#28a745' }}>
                Télécharger PDF <DownOutlined />
              </Button>
            </Dropdown>,
            <Button key="cl" onClick={() => { setModalVisible(false); setSelectedDocument(null); }}>Fermer</Button>,
          ]}
        >
          {selectedDocument && (
            <div id="italian-certificate-document" className="cierha-certificate-modal-content italian-cert-preview-stack">
              <ItalianCertificateDocument
                document={selectedDocument}
                template={template}
                locale="it"
                qrCodeData={qrCodeData}
                defaultAssets={defaultAssets}
              />
              <ItalianCertificateDocument
                document={selectedDocument}
                template={template}
                locale="fr"
                qrCodeData={qrCodeData}
                defaultAssets={defaultAssets}
              />
            </div>
          )}
        </Modal>

        <ItalianTemplateEditorModal
          open={templateModalVisible}
          onClose={() => setTemplateModalVisible(false)}
          onSave={handleSaveTemplate}
          template={template}
          defaultAssets={defaultAssets}
        />
      </div>
    </ConfigProvider>
  );
};

const ItalianCertificateDocument = ({
  document: docData, template, locale = 'fr', qrCodeData, defaultAssets: assets,
}) => {
  const localeTemplate = getItalianLocaleTemplate(template, locale);
  const mergeData = buildItalianMergeData(docData, locale);
  const border = template.borderImageUrl || assets.border;
  const leftLogo = template.leftLogoUrl || assets.leftLogo;
  const rightLogo = template.rightLogoUrl || assets.rightLogo;
  const pageClass = locale === 'it' ? 'italian-cert-page-it' : 'italian-cert-page-fr';

  return (
    <div className={`italian-cert-page ${pageClass}`}>
      <img src={border} alt="" className="italian-cert-border" aria-hidden="true" />
      <div className="italian-cert-content">
        <div className="italian-cert-header">
          <img src={leftLogo} alt="CIREP" className="italian-cert-logo italian-cert-logo-left" />
          <img src={rightLogo} alt="Troia" className="italian-cert-logo italian-cert-logo-right" />
        </div>

        <h1 className="italian-cert-title">{localeTemplate.documentTitle}</h1>
        <p className="italian-cert-deliver">{localeTemplate.deliverPrefix}</p>
        <p className="italian-cert-name">
          {locale === 'fr' ? `${mergeData.genre} ${mergeData.noms}` : mergeData.noms}
        </p>
        <p className="italian-cert-birth" dangerouslySetInnerHTML={{ __html: applyMergeTemplate(localeTemplate.birthLineTemplate, mergeData) }} />
        <p className="italian-cert-body" dangerouslySetInnerHTML={{ __html: applyMergeTemplate(localeTemplate.bodyTemplate, mergeData) }} />
        <p className="italian-cert-date" dangerouslySetInnerHTML={{ __html: applyMergeTemplate(localeTemplate.dateLineTemplate, mergeData) }} />

        <div className="italian-cert-signatures italian-cert-signatures-three">
          <div className="italian-cert-sig-col">
            <p className="italian-cert-sig-name">{localeTemplate.signature1Name}</p>
            <p className="italian-cert-sig-title">{localeTemplate.signature1Title}</p>
            <img src={assets.signature1Image} alt="" className="italian-cert-sig-image italian-cert-sig-image-bap" aria-hidden="true" />
          </div>
          <div className="italian-cert-sig-col">
            <p className="italian-cert-sig-name">{localeTemplate.signature2Name}</p>
            <p className="italian-cert-sig-title">{localeTemplate.signature2Title}</p>
            <img src={assets.signature2Image} alt="" className="italian-cert-sig-image italian-cert-sig-image-bortone" aria-hidden="true" />
          </div>
          <div className="italian-cert-sig-col">
            <p className="italian-cert-sig-name">{localeTemplate.signature3Name}</p>
            <p className="italian-cert-sig-title">{localeTemplate.signature3Title}</p>
            <img src={assets.signature3Image} alt="" className="italian-cert-sig-image italian-cert-sig-image-caserta" aria-hidden="true" />
          </div>
        </div>

        {qrCodeData && <img src={qrCodeData} alt="QR Code" className="cierha-qr-code italian-cert-qr-code" />}
      </div>
    </div>
  );
};

export default ItalianCertificate;
